import {
    NETTIX_DOMAINS,
    NETTIX_TEST_DOMAINS,
    NETTIX_VERTICAL_DOMAINS,
    VERTICAL_BY_NETTIX_DOMAIN,
    VERTICAL_BY_NETTIX_TEST_DOMAINS
} from './constants'
import cookies from 'lib/cookies'
import { isDev } from 'lib/env'
import { isProductionHost } from './env'

/**
 We need to communicate between inbox/omanettix what vertical user landed from.

 Currently other service is oblivious about the vertical and does not display vertical back link when entered from another.

 This will be handled with new cookie called `nettix-vertical-referrer` that inbox/omanettix will set based on this logic:

 ```
 How to set:
 - if user enters from vertical

 How to update (when cookie exists):
 - if user enters from vertical (update with the vertical user entered from)
 - if user enters via inbox/omanettix, then refresh expiration

 How to clear:
 - If user enters from outside of nettix (document.referrer is empty OR does not match our domains)
 - If user logouts
 ```
 */

// const TEST_REFERRER = 'https://www.nettimoto.com/'
// const TEST_REFERRER = 'https://viestit.almaajo.fi/'
const TEST_REFERRER = ''

const VERTICAL_REFERRER_COOKIE_NAME = 'nettix-vertical-referrer'
const VERTICAL_REFERRER_COOKIE_DOMAIN = !isDev() ? '.almaajo.fi' : 'localhost'

/**
 * Util to get vertical based on document.referrer.
 */
export const getReferrerVertical = (referrer) => {
    const domain = referrer && isProductionHost() ? NETTIX_VERTICAL_DOMAINS.find(domain => (
        referrer.toLowerCase().indexOf(`.${domain}`) !== -1 ||
        referrer.toLowerCase().indexOf(`://${domain}`) !== -1
    )) : NETTIX_TEST_DOMAINS.find(domain => (
        referrer.toLowerCase().indexOf(`.${domain}`) !== -1 ||
        referrer.toLowerCase().indexOf(`://${domain}`) !== -1
    ))

    const vertical = isProductionHost() ? VERTICAL_BY_NETTIX_DOMAIN[domain] : VERTICAL_BY_NETTIX_TEST_DOMAINS[domain]
    const url = vertical && domain ? referrer : false

    return url ? {
        url: url,
        name: vertical,
    } : null
}

const getNettixDomain = (url) => {
    return url && NETTIX_DOMAINS.find(domain => (
        url.toLowerCase().indexOf(`.${domain}`) !== -1 ||
        url.toLowerCase().indexOf(`://${domain}`) !== -1
    ))
}

const getReferrerUrlByVertical = (vertical) => {
    // Resolve vertical domain to be used as referrer url
    const entry = Object.entries(VERTICAL_BY_NETTIX_DOMAIN)
        .find(([key, value]) => value === vertical)

    return entry ? `https://${entry[0]}` : null
}

export const handleVerticalReferrer = () => {
    // document.referrer is empty on localhost when entering from outside site, so use test url instead
    const documentReferrer = isDev() ? TEST_REFERRER : document.referrer

    // Store for later use in navigation
    window.__mv_referrer = documentReferrer

    // check if we came outside nettix and clear vertical referrer
    if (documentReferrer === '' || !getNettixDomain(documentReferrer)) {
        clearVerticalReferrer()
        return
    }

    // Get referrer cookie that can be set by Inbox or OmaNettix.
    // The cookie value is in form of vertical name eg. 'car', 'bike'...
    const referrerCookieVertical = cookies.get(VERTICAL_REFERRER_COOKIE_NAME)

    // Try to get vertical url based on vertical name eg. 'car', 'bike'...
    const cookieReferrer = referrerCookieVertical ? getReferrerUrlByVertical(referrerCookieVertical) : null

    const documentReferrerVertical = getReferrerVertical(documentReferrer)
    const cookieReferrerVertical = getReferrerVertical(cookieReferrer)

    const referrerVertical = documentReferrerVertical || cookieReferrerVertical

    if (referrerVertical) {
        window.__mv_referrer = referrerVertical.url
        setVerticalReferrer(referrerVertical.name)
    } else {
        window.__mv_referrer = null
        clearVerticalReferrer()
    }
}

const setVerticalReferrer = (vertical) => {
    // 2 week cookie as SSO session can last for two weeks
    cookies.set(VERTICAL_REFERRER_COOKIE_NAME, vertical, {
        domain: VERTICAL_REFERRER_COOKIE_DOMAIN,
        maxAge: 1209600,
    })
}

export const tryUpdateVerticalReferrer = () => {
    const referrerCookieVertical = cookies.get(VERTICAL_REFERRER_COOKIE_NAME)
    if (referrerCookieVertical) {
        setVerticalReferrer(referrerCookieVertical)
    }
}

export const clearVerticalReferrer = () => {
    window.__mv_referrer = document.referrer

    cookies.remove(VERTICAL_REFERRER_COOKIE_NAME, {
        domain: VERTICAL_REFERRER_COOKIE_DOMAIN,
        path: '/',
    })
}

export const handleVerticalBackUrl = () => {
    let referrer = window.__mv_referrer || document.referrer

    if (referrer !== '' && getNettixDomain(referrer) && !cookies.get('userLoggedIn')) {
        cookies.set('nettix-vertical-back-url', referrer)
    }

    const verticalBackUrl = !!cookies.get('userLoggedIn') === true ? cookies.get('nettix-vertical-back-url') : ''

    if (verticalBackUrl) {
        window.__mv_referrer = referrer = verticalBackUrl
        cookies.remove('nettix-vertical-back-url')
    }

    return referrer
}
